import { useRef, useState } from 'react';

import { api, type SzamlaImportElonezet, type SzamlaImportEredmeny } from '../api.ts';
import { ft } from '../formazas.ts';

/**
 * Számlaimport a számlázó exportjából. (Megbízás 2.0 / 5.4)
 *
 * Az import **nem ír azonnal**: előbb megmutatja, mi történne. A fájl
 * bármikor újra feltölthető — a már meglévő számlákat a bizonylatszám
 * alapján kihagyja, nem duplikálja.
 */
export function SzamlaImport({
  onKesz,
  onMegse,
}: {
  onKesz: () => void;
  onMegse: () => void;
}) {
  const [fajl, setFajl] = useState<File | null>(null);
  const [elonezet, setElonezet] = useState<SzamlaImportElonezet | null>(null);
  const [hiba, setHiba] = useState<string | null>(null);
  const [munkaban, setMunkaban] = useState(false);
  const [kesz, setKesz] = useState<string | null>(null);
  const mezo = useRef<HTMLInputElement>(null);

  async function beolvas(valasztott: File) {
    setMunkaban(true);
    setHiba(null);
    setKesz(null);
    setElonezet(null);
    try {
      setElonezet(
        await api.fajl<SzamlaImportElonezet>('/api/szamla/import/elonezet', valasztott),
      );
      setFajl(valasztott);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A fájlt nem sikerült beolvasni.');
    } finally {
      setMunkaban(false);
    }
  }

  async function vegrehajt() {
    if (!fajl) return;
    setMunkaban(true);
    setHiba(null);
    try {
      const v = await api.fajl<SzamlaImportEredmeny>('/api/szamla/import/vegrehajtas', fajl);
      const reszek = [`${v.felvett} számla felvéve`];
      if (v.frissitett) reszek.push(`${v.frissitett} frissítve`);
      if (v.jovairt) reszek.push(`${v.jovairt} időközben kifizetve`);
      if (v.ujra_parositott) reszek.push(`${v.ujra_parositott} most már párosult`);
      if (v.kihagyott) reszek.push(`${v.kihagyott} változatlan`);
      if (v.stornozott) reszek.push(`${v.stornozott} stornózottnak jelölve`);
      // A stornó a korábban betöltött számlákon is végigfut; amelyikhez
      // mégsem találunk eredetit, azt megnevezzük. (2026-09-25)
      if (v.storno_eredeti_nelkul?.length)
        reszek.push(
          `${v.storno_eredeti_nelkul.length} stornóhoz nincs meg az eredeti számla ` +
            `(${v.storno_eredeti_nelkul.slice(0, 3).join(', ')})`,
        );
      if (v.parositatlan) reszek.push(`${v.parositatlan} telephely nem párosult`);
      if (v.duplikalt.length)
        reszek.push(
          `${v.duplikalt.length} bizonylatszám kétszer volt a fájlban (${v.duplikalt.slice(0, 3).join(", ")}) — csak az első került be`,
        );
      setKesz(reszek.join(' · ') + '.');
      setElonezet(null);
      setFajl(null);
      onKesz();
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'Az import nem sikerült.');
    } finally {
      setMunkaban(false);
    }
  }

  return (
    <div className="tdet" style={{ background: '#fff', borderColor: 'var(--rule2)' }}>
      <h4>Számlák importálása</h4>

      <div className="btns" style={{ marginTop: 0 }}>
        <label className="btn sm" style={{ cursor: 'pointer', margin: 0 }}>
          Fájl kiválasztása
          <input
            ref={mezo}
            type="file"
            accept=".xlsx,.xlsm"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void beolvas(f);
              e.target.value = '';
            }}
          />
        </label>
        {fajl && <span className="hint">{fajl.name}</span>}
        {munkaban && <span className="hint">Dolgozom…</span>}
      </div>

      {!elonezet && !munkaban && !kesz && (
        <p className="hint" style={{ marginTop: 10 }}>
          A számlázó exportja (<code>.xlsx</code>). Az oszlopokat fejléc alapján ismeri fel.
          Ugyanaz a fájl többször is feltölthető — a már meglévő számlákat kihagyja.
        </p>
      )}

      {hiba && <div className="uzenet bad">{hiba}</div>}
      {kesz && <div className="uzenet ok">{kesz}</div>}

      {elonezet && (
        <>
          <div className="cards" style={{ marginTop: 12 }}>
            <div className="card">
              <em>A fájlban</em>
              <b>{ft(elonezet.osszes)}</b>
              <span>számla</span>
            </div>
            <div className="card">
              <em>Új</em>
              <b>{ft(elonezet.uj)}</b>
              <span>bekerül</span>
            </div>
            <div className="card">
              <em>Már megvan</em>
              <b>{ft(elonezet.mar_megvan)}</b>
              <span>kimarad</span>
            </div>
            <div className="card">
              <em>Stornó</em>
              <b>{ft(elonezet.storno)}</b>
              <span>jelölve, nem törölve</span>
            </div>
            <div className="card">
              <em>Készpénzes</em>
              <b>{ft(elonezet.keszpenzes)}</b>
              <span>nem nyitott tétel</span>
            </div>
            <div className={`card${elonezet.parositatlan_telephely > 0 ? ' warn2' : ''}`}>
              <em>Párosítatlan</em>
              <b>{ft(elonezet.parositatlan_telephely)}</b>
              <span>telephely</span>
            </div>
          </div>

          <div className="uzenet" style={{ background: 'var(--bluew)', color: 'var(--ink)' }}>
            <b>{elonezet.munkalap}</b> munkalap. Felismert oszlopok:{' '}
            {elonezet.oszlopok.map((o) => `${o.betu}: ${o.fejlec}`).join(' · ')}
          </div>

          {elonezet.figyelmeztetesek.map((f) => (
            <div className="uzenet flag" key={f}>
              {f}
            </div>
          ))}

          {elonezet.parositatlan_nevek.length > 0 && (
            <>
              <p className="lbl" style={{ marginTop: 12 }}>
                Ezek a telephelyek nem párosultak a törzshöz
              </p>
              <div className="uzenet flag">
                A számlák így is bekerülnek, csak nem tudjuk, melyik éttermünkhöz tartoznak.
                Utólag a listában, a „Párosítatlan" szűrőnél rendezhetők.
              </div>
              <div className="matrix" style={{ maxHeight: 160, overflow: 'auto' }}>
                <table className="tt2">
                  <tbody>
                    {elonezet.parositatlan_nevek.map((n) => (
                      <tr key={n}>
                        <td style={{ padding: '5px 10px', fontSize: 11.5 }}>{n}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <div className="btns">
            <button
              className="btn pri sm"
              disabled={munkaban || elonezet.uj === 0}
              onClick={() => void vegrehajt()}
            >
              {elonezet.uj === 0
                ? 'Minden számla már megvan'
                : `Importálás — ${elonezet.uj} új számla`}
            </button>
            <button className="btn sm" onClick={onMegse}>
              Mégsem
            </button>
          </div>
        </>
      )}
    </div>
  );
}
